import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const spamHighlightKey = new PluginKey("spamHighlight");

const normalizePhrases = (phrases) =>
  (Array.isArray(phrases) ? phrases : [])
    .map((phrase) => String(phrase || "").toLowerCase().trim())
    .filter(Boolean);

const isWordChar = (char) => Boolean(char && /[a-z0-9_]/i.test(char));

const findPhraseRanges = (text, phrase) => {
  const ranges = [];
  if (!text || !phrase) return ranges;

  const lower = text.toLowerCase();
  const needle = phrase.toLowerCase();
  let fromIndex = 0;

  while (fromIndex <= lower.length) {
    const idx = lower.indexOf(needle, fromIndex);
    if (idx === -1) break;

    const before = idx === 0 ? "" : lower[idx - 1];
    const after =
      idx + needle.length >= lower.length ? "" : lower[idx + needle.length];

    if (!isWordChar(before) && !isWordChar(after)) {
      ranges.push({ from: idx, to: idx + needle.length });
    }

    fromIndex = idx + Math.max(needle.length, 1);
  }

  return ranges;
};

const buildSpamDecorations = (doc, phrases) => {
  const normalized = normalizePhrases(phrases);
  if (!normalized.length) {
    return DecorationSet.empty;
  }

  const decorations = [];

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    for (const phrase of normalized) {
      for (const range of findPhraseRanges(node.text, phrase)) {
        decorations.push(
          Decoration.inline(pos + range.from, pos + range.to, {
            class: "spam-word-hit",
            title: `Blocked word: ${phrase}`,
          })
        );
      }
    }
  });

  return DecorationSet.create(doc, decorations);
};

/** Push the latest spam phrase list into the editor decoration plugin. */
export const setSpamHighlightPhrases = (editor, phrases) => {
  if (!editor || editor.isDestroyed) return;
  editor.view.dispatch(
    editor.state.tr.setMeta(spamHighlightKey, normalizePhrases(phrases))
  );
};

/**
 * TipTap extension: highlights blocked spam words in the editor without
 * changing the document content.
 */
const SpamHighlight = Extension.create({
  name: "spamHighlight",

  addOptions() {
    return {
      phrases: [],
    };
  },

  addProseMirrorPlugins() {
    const initialPhrases = normalizePhrases(this.options.phrases);

    return [
      new Plugin({
        key: spamHighlightKey,
        state: {
          init: () => initialPhrases,
          apply(tr, value) {
            const next = tr.getMeta(spamHighlightKey);
            return next !== undefined ? next : value;
          },
        },
        props: {
          decorations(state) {
            const phrases = spamHighlightKey.getState(state) || [];
            return buildSpamDecorations(state.doc, phrases);
          },
        },
      }),
    ];
  },
});

export default SpamHighlight;
